import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, FileText, AlertTriangle, HelpCircle, ArrowRight, Home, Building2, User } from 'lucide-react';

const ProceduresNavigator = () => {
    const [selectedAction, setSelectedAction] = useState(null);

    const actions = [
        {
            id: 'buy',
            title: 'Comprar Propiedad',
            icon: Home,
            description: 'Adquirir una casa, apartamento o terreno.',
            steps: [
                {
                    title: '1. Verificación Legal (Due Diligence)',
                    desc: 'Antes de firmar nada, asegúrate que la propiedad no tenga problemas.',
                    docs: [
                        { name: 'Certificación Extractada (CNR)', why: 'Confirma quién es el dueño real y si tiene hipotecas o embargos.', price: '$8.86' },
                        { name: 'Solvencia Municipal', why: 'Demuestra que no hay deudas de impuestos municipales.', price: '$4.12' },
                        { name: 'Ubicación Catastral', why: 'Verifica que los linderos físicos coincidan con el mapa oficial.', price: '$5.00' }
                    ]
                },
                {
                    title: '2. Formalización de Compraventa',
                    desc: 'El proceso legal de traspaso ante notario.',
                    docs: [
                        { name: 'DUI y NIT (Vigentes)', why: 'Identificación oficial de comprador y vendedor.', price: 'Gratis ($10.31 Reposición)' },
                        { name: 'Escritura Original', why: 'Documento base que el vendedor entrega al notario.', price: 'N/A' },
                        { name: 'Pago de Impuesto de Transferencia', why: '3% sobre el excedente de $28,571.43.', price: '3% s/excedente' }
                    ]
                },
                {
                    title: '3. Inscripción en CNR',
                    desc: 'Hacer público tu derecho de propiedad.',
                    docs: [
                        { name: 'Testimonio de Escritura', why: 'La copia oficial que te entrega el notario.', price: 'Honorarios' },
                        { name: 'Pago de Derechos de Registro', why: 'Arancel calculado sobre el precio de venta.', price: '$0.63 x $100' }
                    ]
                }
            ]
        },
        {
            id: 'sell',
            title: 'Vender Propiedad',
            icon: Building2,
            description: 'Traspasar tu inmueble a un nuevo dueño.',
            steps: [
                {
                    title: '1. Preparación de Documentos',
                    desc: 'Ten todo listo para no perder compradores.',
                    docs: [
                        { name: 'Escritura Registrada', why: 'Prueba que eres el dueño legítimo.', price: 'N/A' },
                        { name: 'Solvencia Municipal (Vigente)', why: 'Requisito obligatorio para firmar la venta.', price: '$3.50 aprox.' },
                        { name: 'DUI y NIT (Homologado)', why: 'Deben estar vigentes y en buen estado.', price: 'Gratis' }
                    ]
                },
                {
                    title: '2. Pago de Impuestos (Ganancia de Capital)',
                    desc: 'Si vendes más caro de lo que compraste.',
                    docs: [
                        { name: 'Declaración de Renta', why: 'Se paga el 10% sobre la ganancia neta de la venta.', price: '10% Ganancia' }
                    ]
                }
            ]
        },
        {
            id: 'mortgage',
            title: 'Hipotecar / Financiar',
            icon: Badge,
            description: 'Obtener un crédito poniendo tu propiedad en garantía.',
            steps: [
                {
                    title: '1. Pre-calificación Bancaria',
                    desc: 'Saber cuánto te prestan según tus ingresos.',
                    docs: [
                        { name: 'Constancia de Salario / Renta', why: 'Demuestra tu capacidad de pago.', price: 'Gratis' },
                        { name: 'Estado de Cuenta AFP', why: 'Verifica estabilidad laboral.', price: 'Gratis' },
                        { name: 'Recibos de Servicios', why: 'Comprobante de domicilio actual.', price: 'Variable' }
                    ]
                },
                {
                    title: '2. Valúo de la Propiedad',
                    desc: 'El banco revisa cuánto vale la casa.',
                    docs: [
                        { name: 'Copia de Escritura', why: 'Para que el perito verifique medidas.', price: 'N/A' },
                        { name: 'Pago de Valúo', why: 'Costo administrativo del banco.', price: '$150 - $300' }
                    ]
                }
            ]
        }
    ];

    const activeContent = actions.find(a => a.id === selectedAction);

    return (
        <div className="space-y-8">
            {/* Action Selection */}
            {!selectedAction ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {actions.map((action) => (
                        <Card
                            key={action.id}
                            className="cursor-pointer hover:shadow-lg transition-all border-2 border-transparent hover:border-primary group"
                            onClick={() => setSelectedAction(action.id)}
                        >
                            <CardContent className="p-8 text-center space-y-4">
                                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto group-hover:bg-primary group-hover:text-white transition-colors">
                                    <action.icon size={32} className="text-primary group-hover:text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-800">{action.title}</h3>
                                <p className="text-sm text-gray-600">{action.description}</p>
                                <Button variant="ghost" className="text-primary group-hover:bg-white/10">
                                    Ver Requisitos <ArrowRight size={16} className="ml-2" />
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <div className="animate-in fade-in slide-in-from-right-8 duration-300">
                    <Button
                        variant="ghost"
                        onClick={() => setSelectedAction(null)}
                        className="mb-6 hover:bg-gray-100"
                    >
                        ← Volver a Trámites
                    </Button>

                    <div className="bg-white rounded-xl shadow-sm border p-8">
                        <div className="flex items-center gap-4 mb-8 pb-6 border-b">
                            <div className="p-3 bg-primary/10 rounded-lg">
                                {activeContent && <activeContent.icon size={32} className="text-primary" />}
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">{activeContent.title}</h2>
                                <p className="text-gray-600">Guía paso a paso de documentos y procesos</p>
                            </div>
                        </div>

                        <div className="space-y-8">
                            {activeContent.steps.map((step, idx) => (
                                <div key={idx} className="relative pl-8 border-l-2 border-gray-200 last:border-0 pb-8 last:pb-0">
                                    <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-primary border-4 border-white shadow-sm"></div>
                                    <h3 className="text-lg font-bold text-gray-800 mb-1">{step.title}</h3>
                                    <p className="text-sm text-gray-500 mb-4">{step.desc}</p>

                                    <div className="grid gap-3">
                                        {step.docs.map((doc, i) => (
                                            <div key={i} className="bg-gray-50 p-4 rounded-lg border border-gray-100 flex gap-4 items-start hover:bg-blue-50 transition-colors">
                                                <FileText className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                                                <div className="flex-grow">
                                                    <div className="flex justify-between items-start">
                                                        <span className="font-semibold text-gray-900 block">{doc.name}</span>
                                                        {doc.price && (
                                                            <Badge variant="outline" className="text-xs bg-white whitespace-nowrap ml-2 border-green-200 text-green-700">
                                                                {doc.price}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <span className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                                        <HelpCircle size={12} className="text-primary" /> ¿Por qué? {doc.why}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex gap-3">
                            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                            <p className="text-sm text-yellow-800">
                                <strong>Nota Importante:</strong> Esta es una guía general. Dependiendo de la complejidad del caso (herencias, donaciones, empresas), pueden requerirse documentos adicionales. Te recomendamos asesorarte con nuestros expertos.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProceduresNavigator;
